// src/components/LayerPanel/Tabs.jsx
//
// Top-aligned pill tab bar. Sticky directly under the panel header (it sits in
// the panel's flex column, so it doesn't scroll). Per-tab active-count badges
// are intentionally omitted for now.
//
// Pills are content-width, not equal-width: each takes what its label needs and
// they're spread across the bar with justify-between. Five of them is a tight
// fit in the 370px panel (MapPage.jsx sets it to calc(20rem + 50px)).
//
// The budget, so the next person editing a label can do the arithmetic: 370px
// less `p-1` either side and four `gap-1` gaps leaves ~346px for the buttons,
// and each button spends 37px on chrome before a glyph is drawn — `px-2` (16),
// the 1px border either side (2), the 13px icon, and `gap-1.5` (6). That left
// ~161px of text across five labels, which is what forced "My Layers"→"Mine"
// and "Reference"→"Ref".
//
// Two of those numbers are now tighter — `px-1.5` and `gap-1` — freeing ~30px,
// which is what pays for "My Layers" going back to its full name. Both are
// invisible at 10px type. "Ref" still needs ~13px it doesn't have.
//
// flex-wrap stays as the safety net: if a label grows or a wider font loads, a
// second row degrades better than a clipped label.

function cx(...c) { return c.filter(Boolean).join(" "); }

export default function Tabs({ tabs, activeTab, onChange }) {
  return (
    <div
      role="tablist"
      aria-label="Layer catalog scopes"
      className="flex-shrink-0 flex flex-wrap items-center justify-between gap-1 p-1 border-b border-slate-800 bg-bg-softer"
    >
      {tabs.map((tab) => {
        const active = tab.id === activeTab;
        const Icon = tab.icon;
        return (
          <button
            key={tab.id}
            role="tab"
            aria-selected={active}
            onClick={() => onChange(tab.id)}
            className={cx(
              "flex items-center justify-center gap-1 px-1.5 py-1 whitespace-nowrap",
              "text-[10px] font-semibold uppercase tracking-wide",
              "rounded-lg border transition-colors",
              // Selected tab becomes a mini PadRekko-header panel: the same
              // #102536 fill + slate border, rounded, with the teal carried by
              // the label. Every tab holds the same 1px border (transparent
              // when inactive) so selection never shifts the height.
              active
                ? "bg-[#102536] border-slate-700 text-accent-action"
                : "bg-transparent border-transparent text-fg-secondary hover:text-gray-300 hover:bg-slate-800/30"
            )}
          >
            {Icon && <Icon size={13} className="flex-shrink-0" />}
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}
